import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { RouteComponentProps } from 'react-router'
import { useHistory } from 'react-router-dom'
import { parse } from 'query-string'

import {
  SearchResultsPage,
  useSearchResultsPage,
  DEFAULT_MAP_STYLE,
} from '@centivo/provider-directory-components'
import type {
  DirectorySpecialtyOption,
  FilterState,
  ResultsToolbarSelection,
  SearchResultItem,
  SearchResultsBanner,
} from '@centivo/provider-directory-components'

import { CATEGORY } from 'src/refactor/helpers/constants'
import { buildProfileLocation } from 'src/refactor/helpers/profileNavigation'
import { useDirectoryAdapter } from 'src/refactor/hooks'

import {
  createSmartSuggestionFetcher,
  fetchDirectorySpecialties,
  DIRECTORY_LANGUAGES_LIST,
} from './functions/searchBarAdapters'
import { decodeResultsSearch } from './functions/resultsSearchParams'
import {
  loadResultsUiState,
  saveResultsUiState,
} from './functions/resultsUiState'
import { ResultsSearchBar } from './ResultsSearchBar'
import { StyledResultsPage, StyledSearchLanding } from './Results.styled'

const RESULTS_PER_PAGE = 30

const getParam = (
  params: Record<string, string | string[] | null>,
  key: string
): string => {
  const value = params[key]
  if (Array.isArray(value)) return value[0] ?? ''
  return value ?? ''
}

export const Results: React.FC<RouteComponentProps> = ({ location }) => {
  const history = useHistory()

  const urlParams = useMemo(
    () => parse(location.search) as Record<string, string | string[] | null>,
    [location.search]
  )

  const category = getParam(urlParams, 'category') || CATEGORY.DOCTOR

  const fetchSmartSuggestion = useMemo(() => createSmartSuggestionFetcher(), [])

  const [specialtiesList, setSpecialtiesList] = useState<
    DirectorySpecialtyOption[]
  >([])
  useEffect(() => {
    let active = true
    fetchDirectorySpecialties()
      .then(list => {
        if (active) setSpecialtiesList(list)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [])

  // The search bar (rendered above) owns the input and navigates here with the
  // search encoded in the URL; the page derives its search purely from those
  // params.
  const searchParams = useMemo(
    () => decodeResultsSearch(urlParams),
    [urlParams]
  )

  const { directoryAdapter, mapboxAccessToken } = useDirectoryAdapter({
    searchParams,
  })

  const banner = useMemo<SearchResultsBanner | undefined>(() => {
    if (!searchParams) return undefined
    const contextLabel =
      searchParams.selectedServiceLine?.label ??
      searchParams.selectedCondition?.label ??
      (searchParams.query.trim() || 'All providers')
    return {
      contextLabel,
      searchedQuery: searchParams.query.trim() || contextLabel,
    }
  }, [searchParams])

  const resultsSummary = useMemo(() => {
    if (!searchParams) return ''
    return searchParams.zipCode
      ? `Showing matches near ${searchParams.zipCode}`
      : 'Showing matches'
  }, [searchParams])

  // Persist filters/toolbar per search so leaving for a profile page and coming
  // back (which remounts this route) restores what the user had applied. Keyed
  // by the search query string: a new search starts fresh, the same search
  // restores. Read once on mount for this search.
  const persistenceKey = location.search
  const storedUiState = useMemo(
    () => loadResultsUiState(persistenceKey),
    [persistenceKey]
  )

  const initialFilters = useMemo<Partial<FilterState>>(
    () =>
      storedUiState?.filters ?? {
        providerType: category === CATEGORY.FACILITY ? 'institutions' : 'all',
      },
    [storedUiState, category]
  )

  const onFilterChange = useCallback(
    (filters: FilterState) => {
      saveResultsUiState(persistenceKey, { filters })
    },
    [persistenceKey]
  )

  const onSelectionChange = useCallback(
    (selection: ResultsToolbarSelection) => {
      saveResultsUiState(persistenceKey, {
        careType: selection.careType,
        sortBy: selection.sortBy,
      })
    },
    [persistenceKey]
  )

  const { searchResultsPage } = useSearchResultsPage({
    searchParams,
    fetchSearchResults: directoryAdapter.fetchSearchResults,
    fetchProviderLocations: directoryAdapter.fetchProviderLocations,
    fetchInstitutionProviders: directoryAdapter.fetchInstitutionProviders,
    fetchSmartSuggestion,
    resultsPerPage: RESULTS_PER_PAGE,
    banner,
    specialtiesList,
    languagesList: DIRECTORY_LANGUAGES_LIST,
    mapboxAccessToken,
    mapStyle: DEFAULT_MAP_STYLE,
    resultsSummary,
    initialFilters,
    initialCareType: storedUiState?.careType,
    initialSortBy: storedUiState?.sortBy,
    onFilterChange,
    onSelectionChange,
  })

  const itemsRef = useRef<SearchResultItem[]>([])

  useEffect(() => {
    itemsRef.current = searchResultsPage.items
  }, [searchResultsPage.items])

  const onSelectProvider = useCallback(
    (id: string) => {
      const found = itemsRef.current.find(
        item => item.kind === 'practitioner' && item.data.id === id
      )
      const distance =
        found?.kind === 'practitioner'
          ? found.data.primaryLocation.distanceMiles
          : undefined
      history.push(
        buildProfileLocation('doctor', id, {
          fetchParams: searchResultsPage.fetchParams,
          distance,
          placeId: searchParams?.placeId,
        })
      )
    },
    [history, searchParams?.placeId, searchResultsPage.fetchParams]
  )

  const onSelectInstitution = useCallback(
    (id: string) => {
      const found = itemsRef.current.find(
        item => item.kind === 'institution' && item.data.id === id
      )
      const distance =
        found?.kind === 'institution'
          ? found.data.location.distanceMiles
          : undefined
      history.push(
        buildProfileLocation('institution', id, {
          fetchParams: searchResultsPage.fetchParams,
          distance,
        })
      )
    },
    [history, searchResultsPage.fetchParams]
  )

  useEffect(() => {
    document.title = 'Centivo Provider Search - Results page'
    document.getElementById('aria-field')?.focus()
    const paginationUl = document.querySelector('.pagination-container ul')
    if (paginationUl) paginationUl.removeAttribute('role')
  }, [])

  return (
    <>
      <ResultsSearchBar />
      <StyledResultsPage>
        {searchParams ? (
          <SearchResultsPage
            {...searchResultsPage}
            onSelectProvider={onSelectProvider}
            onSelectInstitution={onSelectInstitution}
          />
        ) : (
          <StyledSearchLanding>
            <h1>Find a provider</h1>
            <p>
              Use the search bar above to type a specialty, condition or zip
              code, choose an autocomplete suggestion, then click Search.
            </p>
          </StyledSearchLanding>
        )}
      </StyledResultsPage>
    </>
  )
}
